import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, User, MapPin, Calendar, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import SEO from '../../components/SEO';

const CausesRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    age: '',
    city: '',
    bio: ''
  });
  const [error, setError] = useState('');

  const API = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/api/petitions/auth/register`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        age: formData.age ? parseInt(formData.age) : null,
        city: formData.city,
        bio: formData.bio || null,
        auth_method: 'email_password'
      });

      // Store token and user data
      localStorage.setItem('causes_token', response.data.access_token);
      localStorage.setItem('causes_user', JSON.stringify(response.data.campaigner));

      toast.success('Registration successful! Welcome to Elderra Causes.');
      navigate('/causes/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="causes-register-page">
      <SEO title="Register - Elderra Causes" description="Create your Elderra Causes account" />

      <div className="causes-register-container">
        <div className="causes-register-card">
          <Link to="/causes" className="back-link">
            <ArrowLeft size={20} />
            Back to Home
          </Link>

          <div className="causes-register-header">
            <img 
              src="https://customer-assets.emergentagent.com/job_digital-seniors-3/artifacts/fqevhgzz_Elderra%20logo%20new%20original%20%281%29.png" 
              alt="Elderra Logo"
              className="causes-register-logo"
            />
            <h1>Join Elderra Causes</h1>
            <p>Create your account to start a petition</p>
          </div>

          {error && (
            <div className="causes-error-alert">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="causes-register-form">
            <div className="form-row">
              <div className="form-group">
                <label>
                  <User size={18} />
                  Full Name *
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your full name"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>
                  <Calendar size={18} />
                  Age (Optional)
                </label>
                <Input
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  placeholder="Your age"
                  min="1"
                  max="120"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <Mail size={18} />
                  Email Address *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your.email@example.com"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>
                  <Mail size={18} />
                  Mobile Number
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Your mobile number"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                <MapPin size={18} />
                City *
              </label>
              <Input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Your city"
                required
                disabled={loading}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <Lock size={18} />
                  Password *
                </label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>
                  <Lock size={18} />
                  Confirm Password *
                </label>
                <Input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="Re-enter password"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                About You (Optional)
              </label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Tell us a little about yourself (this will be visible on your petitions)"
                rows={3}
                disabled={loading}
              />
            </div>

            <Button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="causes-register-footer">
            <p>Already have an account?</p>
            <Link to="/causes/login" className="login-link">
              Login Here
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CausesRegister;
